<script setup lang="ts">
// The one and only `<EmojiPicker>` instance in the app. Mounted once from `app.vue`; every emoji
// popover (`ComposerEmojiPicker.vue`, `EmojiReactionPopover.vue`) borrows it through
// `useSharedEmojiPicker` instead of rendering its own. See that file for why.
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {Picker as EmojiPicker, Skins as EmojiSkins} from 'emoji-mart-vue-fast/src'
import {emojiIndex, emojiI18n} from '~/lib/chat/emoji'
import {useSharedEmojiPicker} from '~/composables/chat/useSharedEmojiPicker'
import '~/assets/emoji-picker-theme.css'

interface EmojiMartEmoji {
  native: string
}

interface HoveredEmoji {
  x: number
  y: number
  native: string
  name: string
}

const {hostEl, isBuilt, build, emitSelect} = useSharedEmojiPicker()

const stashRef = ref<HTMLElement | null>(null)
const wrapperRef = ref<HTMLElement | null>(null)
const hoveredEmoji = ref<HoveredEmoji | null>(null)

// Whichever popover currently owns the picker, else the hidden stash. `display: none` on the stash
// is what makes parking free: the nodes stay built but cost no layout or paint while parked.
const teleportTarget = computed<HTMLElement | null>(() => hostEl.value ?? stashRef.value)

// Building the picker takes the better part of a second of main-thread time, so it's done during
// idle rather than on the tap that opens it. `requestIdleCallback` isn't available in every
// WKWebView this app ships in (it's a relatively recent Safari addition), hence the timer fallback
// — the exact moment doesn't matter, only that it lands after startup and before the first tap.
// A claimer that gets there first calls `build()` itself, so a missed prewarm only costs that one
// open its old slow path.
const PREWARM_DELAY_MS = 4000
let cancelPrewarm: (() => void) | null = null

onMounted(() => {
  const requestIdle = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
    cancelIdleCallback?: (handle: number) => void
  }).requestIdleCallback

  if (typeof requestIdle === 'function') {
    const handle = requestIdle(() => build(), {timeout: PREWARM_DELAY_MS})
    cancelPrewarm = () => (window as unknown as { cancelIdleCallback?: (h: number) => void })
        .cancelIdleCallback?.(handle)
  } else {
    const handle = window.setTimeout(build, PREWARM_DELAY_MS)
    cancelPrewarm = () => window.clearTimeout(handle)
  }
})

onBeforeUnmount(() => cancelPrewarm?.())

// The picker is never destroyed now, so its own state (search query, scroll position) would
// otherwise carry over from one popover to the next — reopening it half an hour later on a stale
// "pizza" search is not what anyone expects. Reset once it's parked, i.e. after the close, so the
// work never lands on the frame that opens it. Driven through the search input's own `input` event
// because the query lives in `search.vue`'s local `value` (bound with `v-model`), which nothing
// outside the library can write to directly.
watch(hostEl, (el) => {
  if (el) return
  const input = wrapperRef.value?.querySelector<HTMLInputElement>('.emoji-mart-search input')
  if (input?.value) {
    input.value = ''
    input.dispatchEvent(new Event('input', {bubbles: true}))
  }
  const scroll = wrapperRef.value?.querySelector<HTMLElement>('.emoji-mart-scroll')
  if (scroll) scroll.scrollTop = 0
  hoveredEmoji.value = null
})

// The library only tells `Preview.vue`'s fixed bottom bar *which* emoji is hovered, not where it
// is on screen, so a tooltip that actually follows the cursor needs its own delegated listener.
// `mouseover`/`mouseout` (unlike `mouseenter`/`mouseleave`) bubble, so one pair on the wrapper
// covers every emoji button without per-button listeners.
function handleMouseOver(event: MouseEvent) {
  const target = (event.target as HTMLElement).closest<HTMLElement>('.emoji-mart-emoji')
  const wrapper = wrapperRef.value
  if (!target || !wrapper) return

  // `aria-label` is always "<native>, <short_name>, ..." regardless of the `emojiTooltip` prop
  // (see Emoji.vue / EmojiView.ariaLabel), so it doubles as our data source here.
  const [native, name] = (target.getAttribute('aria-label') ?? '').split(', ')
  if (!native) return

  const wrapperRect = wrapper.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()
  hoveredEmoji.value = {
    x: targetRect.left - wrapperRect.left + targetRect.width / 2,
    y: targetRect.top - wrapperRect.top,
    native,
    name: name ?? '',
  }
}

function handleMouseOut(event: MouseEvent) {
  const related = event.relatedTarget as HTMLElement | null
  if (related?.closest('.emoji-mart-emoji')) return
  hoveredEmoji.value = null
}

function onSelect(emoji: EmojiMartEmoji) {
  emitSelect(emoji.native)
}
</script>

<template>
  <!-- Parking spot for the picker while no popover owns it. Kept out of the layout entirely
       (`display: none`) so a built-but-idle picker costs nothing to have around. -->
  <div ref="stashRef" class="hidden" aria-hidden="true"/>

  <!-- `to` swaps between the stash and the open popover's host div; `<Teleport>` *moves* the
       existing DOM rather than re-creating it, which is the entire point of this component. The
       `v-if` only ever goes false→true (see `isBuilt`), so the picker is never torn down. -->
  <Teleport v-if="isBuilt && teleportTarget" :to="teleportTarget">
    <div ref="wrapperRef" class="relative" @mouseover="handleMouseOver" @mouseout="handleMouseOut">
      <EmojiPicker :data="emojiIndex" :i18n="emojiI18n" color="var(--primary)" native @select="onSelect">
        <template #previewTemplate="{ showSkinTones, skinProps, onSkinChange }">
          <div v-if="showSkinTones" class="emoji-mart-preview emoji-mart-preview-skins-only">
            <EmojiSkins :skin="skinProps.skin" @change="onSkinChange"/>
          </div>
        </template>
      </EmojiPicker>

      <div
          v-if="hoveredEmoji"
          class="emoji-hover-tooltip"
          :style="{ left: `${hoveredEmoji.x}px`, top: `${hoveredEmoji.y}px` }"
      >
        <span class="text-base leading-none">{{ hoveredEmoji.native }}</span>
        <span v-if="hoveredEmoji.name">{{ hoveredEmoji.name }}</span>
      </div>
    </div>
  </Teleport>
</template>
